import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { projects } from "../data/projectsData";
import { Helmet } from "react-helmet";
import GridOverlay from "../components/ui/GridOverlay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faReact,
    faNodeJs,
    faStripeS,
    faVuejs,
    faJs,
    faAws,
    faDocker,
    faGithub,
    faPython,
    faJava,
    faPhp,
    faAngular,
} from "@fortawesome/free-brands-svg-icons";
import SEO from "../components/SEO";

// Technology logos map using FontAwesome icons
const techLogos = {
    React: <FontAwesomeIcon icon={faReact} className="w-8 h-8" />,
    "Node.js": <FontAwesomeIcon icon={faNodeJs} className="w-8 h-8" />,
    Stripe: <FontAwesomeIcon icon={faStripeS} className="w-8 h-8" />,
    "Vue.js": <FontAwesomeIcon icon={faVuejs} className="w-8 h-8" />,
    JavaScript: <FontAwesomeIcon icon={faJs} className="w-8 h-8" />,
    AWS: <FontAwesomeIcon icon={faAws} className="w-8 h-8" />,
    Docker: <FontAwesomeIcon icon={faDocker} className="w-8 h-8" />,
    GitHub: <FontAwesomeIcon icon={faGithub} className="w-8 h-8" />,
    Python: <FontAwesomeIcon icon={faPython} className="w-8 h-8" />,
    Java: <FontAwesomeIcon icon={faJava} className="w-8 h-8" />,
    PHP: <FontAwesomeIcon icon={faPhp} className="w-8 h-8" />,
    Angular: <FontAwesomeIcon icon={faAngular} className="w-8 h-8" />,
    Express: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path d="M24 18.588a1.529 1.529 0 01-1.895-.72l-3.45-4.771-.5-.667-4.003 5.444a1.466 1.466 0 01-1.802.708l5.158-6.92-4.798-6.251a1.595 1.595 0 011.9.666l3.576 4.83 3.596-4.81a1.435 1.435 0 011.788-.668L21.708 7.9l-2.522 3.283a.666.666 0 000 .994l4.804 6.412zM.002 11.576l.42-2.075c1.154-4.103 5.858-5.81 9.094-3.27 1.895 1.489 2.368 3.597 2.275 5.973H1.116C.943 16.447 4.005 19.009 7.92 17.7a4.078 4.078 0 002.582-2.876c.207-.666.548-.78 1.174-.588a5.417 5.417 0 01-2.589 3.957 6.272 6.272 0 01-7.306-.933 6.575 6.575 0 01-1.64-3.858c0-.235-.08-.455-.134-.666A88.33 88.33 0 010 11.577zm1.127-.286h9.654c-.06-3.076-2.001-5.258-4.59-5.278-2.882-.04-4.944 2.094-5.071 5.264z" />
        </svg>
    ),
    "D3.js": (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path d="M13.312 12C13.312 5.718 8.22.625 1.937.625H0v5h1.938c3.521 0 6.375 2.854 6.375 6.375s-2.854 6.375-6.375 6.375H0v5h1.938c6.281 0 11.374-5.093 11.374-11.375zM24 7.563C24 3.731 20.893.625 17.062.625h-3.124v5h3.124c1.065 0 1.938.873 1.938 1.938 0 1.064-.873 1.937-1.938 1.937h-1.938c-3.874 0-7.062 3.188-7.062 7.063 0 3.875 3.188 7.062 7.062 7.062h3.125v-5h-3.125c-1.134 0-2.062-.928-2.062-2.062 0-1.135.928-2.063 2.062-2.063h1.938c3.832 0 6.938-3.106 6.938-6.937z" />
        </svg>
    ),
    "Three.js": (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path d="M.38 0a.268.268 0 0 0-.256.332l2.894 11.716a.268.268 0 0 0 .01.04l2.89 11.708a.268.268 0 0 0 .518 0l2.89-11.708a.268.268 0 0 0 .01-.04L12.23.332A.268.268 0 0 0 11.974 0zm14.56 5.317a.268.268 0 0 0-.145.058L8.656 11.071a.269.269 0 0 0-.108.217v5.292a.268.268 0 0 0 .108.217l6.14 5.696a.267.267 0 0 0 .362-.001l6.139-5.696a.268.268 0 0 0 .107-.217v-5.292a.269.269 0 0 0-.107-.217l-6.14-5.696a.267.267 0 0 0-.217-.057zm-7.831 7.581a.27.27 0 0 0-.145.057l-6.14 5.696a.271.271 0 0 0 0 .433l6.14 5.696a.27.27 0 0 0 .362 0l6.14-5.696a.271.271 0 0 0 0-.433l-6.14-5.696a.27.27 0 0 0-.217-.057z" />
        </svg>
    ),
    GSAP: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path d="M20.4 1.8H8.24L6.12 5.24h9.36L2.04 22.2h7.32l3.96-5.88h-3.6L20.4 1.8z" />
        </svg>
    ),
    IoT: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path d="M5 16c0 1.657 2.343 3 5.25 3V13c-2.907 0-5.25 1.343-5.25 3zm7-15v18c2.907 0 5.25-1.343 5.25-3V4c0-1.657-2.343-3-5.25-3zM10.25 1c-2.907 0-5.25 1.343-5.25 3s2.343 3 5.25 3 5.25-1.343 5.25-3-2.343-3-5.25-3zM19 10c0 1.657 2.343 3 5.25 3V7c-2.907 0-5.25 1.343-5.25 3zm5.25-9c-2.907 0-5.25 1.343-5.25 3s2.343 3 5.25 3 5.25-1.343 5.25-3-2.343-3-5.25-3zm0 12c-2.907 0-5.25 1.343-5.25 3s2.343 3 5.25 3 5.25-1.343 5.25-3-2.343-3-5.25-3zM3.5 4c0-1.657-2.343-3-5.25-3v6c2.907 0 5.25-1.343 5.25-3zm0 12c0-1.657-2.343-3-5.25-3v6c2.907 0 5.25-1.343 5.25-3zm-3-6c-2.907 0-5.25 1.343-5.25 3s2.343 3 5.25 3 5.25-1.343 5.25-3-2.343-3-5.25-3z" />
        </svg>
    ),
    WebSockets: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
            <path d="M4.8 12l-4.8 6h4.8v6h6v-6h4.8l-4.8-6 4.8-6h-4.8v-6h-6v6h-4.8l4.8 6zm16.763-3.6c-2.438 3.374-2.211 7.949.676 11.06l1.61-1.611c-1.918-1.919-2.38-4.812-.856-7.193l-1.43-2.256zm-1.703 2.664c-1.238 1.71-1.123 4.034.342 5.5l1.598-1.598c-.344-.345-.546-.808-.546-1.292 0-.38.121-.741.342-1.04l-1.736-1.57zm3.557-5.536c-3.703 4.282-3.703 10.28 0 14.562l1.598-1.598c-2.886-3.16-2.886-8.204 0-11.366l-1.598-1.598z" />
        </svg>
    ),
};

// TechCard component to display a single technology with hover effect
const TechCard = ({ tech }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            className="relative"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            whileHover={{
                scale: 1.04,
                transition: { duration: 0.2, ease: "easeOut" },
            }}
        >
            {/* Gradient border container - larger than the content to account for scaling */}
            <div
                className={`absolute inset-[-3px] rounded-xl transition-opacity duration-300 ${
                    isHovered ? "opacity-100" : "opacity-0"
                }`}
                style={{
                    background:
                        "linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899)",
                }}
            ></div>

            <div className="relative z-10 flex flex-col items-center justify-center p-4 h-24 w-24 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
                <div
                    className={`text-gray-700 transition-colors duration-300 ${
                        isHovered ? "text-gradient" : ""
                    }`}
                >
                    {techLogos[tech] || (
                        <span className="text-xl font-semibold">
                            {tech.charAt(0)}
                        </span>
                    )}
                </div>
                <span className="mt-2 text-xs font-medium text-gray-700">
                    {tech}
                </span>
            </div>
        </motion.div>
    );
};

const ProjectDetail = () => {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);

        // Find the project by ID
        const projectId = parseInt(id);
        const foundProject = projects.find((p) => p.id === projectId);

        // Load immediately without delay
        setProject(foundProject);
        setLoading(false);
    }, [id]);

    // Simple handler to use the stored scroll position when going back
    const handleBackClick = () => {
        // The scroll position was stored when the user clicked on a project
        // We don't need to do anything special here
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center">
                <h1 className="text-3xl font-bold mb-4">Projet non trouvé</h1>
                <Link to="/" className="text-primary-500 hover:underline">
                    Retourner à l'accueil
                </Link>
            </div>
        );
    }

    return (
        <>
            <GridOverlay />
            <main className="pt-32 pb-20">
                <div className="container mx-auto px-4">
                    <motion.div
                        className="mb-2 text-gray-900 font-medium rounded-full transition-all space-x-2 group"
                        whileHover={{
                            scale: 1.03,
                        }}
                    >
                        <Link
                            to="/"
                            className="text-gray-600 hover:text-gray-800 border-b border-transparent hover:border-gray-800 flex items-center transition-all duration-300 ease-in-out hover:translate-x-[-5px] group pb-1"
                            style={{ width: "fit-content" }}
                            onClick={handleBackClick}
                        >
                            <svg
                                className="w-5 h-5 mr-2 transition-transform duration-300 group-hover:translate-x-[-3px]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                                ></path>
                            </svg>
                            Retour au portfolio
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-12"
                    >
                        <div className="order-2 lg:order-1">
                            <div className="mb-6 mt-4">
                                <motion.span
                                    className="bg-black/80 text-white text-xs md:text-sm px-3 sm:px-6 py-1.5 sm:py-2.5 uppercase tracking-wider font-medium shadow-lg rounded-md inline-block text-center"
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                >
                                    {project.category}
                                </motion.span>
                            </div>
                            <div>
                                <motion.h2
                                    className="text-4xl lg:text-5xl font-bold mb-4"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    <span>{project.title}</span>{" "}
                                </motion.h2>
                            </div>

                            <motion.p
                                className="text-gray-600 mb-8 mt-10 text-lg"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.3 }}
                                data-nosnippet
                            >
                                {project.description}
                            </motion.p>

                            <motion.div
                                className="mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.4 }}
                            >
                                <h2 className="text-xl font-bold mb-4">
                                    Technologies utilisées
                                </h2>
                                <div className="flex flex-wrap gap-4">
                                    {project.tags.map((tag, index) => (
                                        <TechCard key={index} tech={tag} />
                                    ))}
                                </div>
                            </motion.div>

                            {/* Project details section */}
                            <motion.div
                                className="mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.5 }}
                            >
                                <h2 className="text-xl font-bold mb-4">
                                    Caractéristiques du projet
                                </h2>
                                <ul className="space-y-3 text-gray-600">
                                    {project.features?.map((feature, index) => (
                                        <li
                                            key={index}
                                            className="flex items-start"
                                        >
                                            <svg
                                                className="w-5 h-5 text-primary-500 mr-2 mt-1 flex-shrink-0"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            <span>{feature}</span>
                                        </li>
                                    )) || (
                                        <>
                                            <li className="flex items-start">
                                                <svg
                                                    className="w-5 h-5 text-primary-500 mr-2 mt-1 flex-shrink-0"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                                <span>
                                                    Interface utilisateur
                                                    réactive et intuitive
                                                </span>
                                            </li>
                                            <li className="flex items-start">
                                                <svg
                                                    className="w-5 h-5 text-primary-500 mr-2 mt-1 flex-shrink-0"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                                <span>
                                                    Architecture backend robuste
                                                    et évolutive
                                                </span>
                                            </li>
                                            <li className="flex items-start">
                                                <svg
                                                    className="w-5 h-5 text-primary-500 mr-2 mt-1 flex-shrink-0"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                                <span>
                                                    Intégration complète avec
                                                    des APIs tierces
                                                </span>
                                            </li>
                                            <li className="flex items-start">
                                                <svg
                                                    className="w-5 h-5 text-primary-500 mr-2 mt-1 flex-shrink-0"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                                <span>
                                                    Solution entièrement
                                                    personnalisée selon les
                                                    besoins du client
                                                </span>
                                            </li>
                                        </>
                                    )}
                                </ul>
                            </motion.div>

                            {/* Project outcomes section */}
                            <motion.div
                                className="mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.6 }}
                            >
                                <h2 className="text-xl font-bold mb-4">
                                    Résultats du projet
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {project.outcomes ? (
                                        project.outcomes.map((outcome, idx) => (
                                            <motion.div
                                                key={idx}
                                                initial={{ opacity: 0, y: 15 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{
                                                    delay: 0.6 + idx * 0.1,
                                                }}
                                                className="bg-gray-50 p-5 rounded-xl hover:shadow-md transition-shadow border border-gray-100"
                                            >
                                                <div className="font-bold text-gray-800 text-2xl mb-1">
                                                    {outcome.value}
                                                </div>
                                                <div className="text-sm font-medium text-gray-600">
                                                    {outcome.name}
                                                </div>
                                                <div className="mt-2 text-xs text-gray-500">
                                                    {outcome.description}
                                                </div>
                                            </motion.div>
                                        ))
                                    ) : (
                                        <>
                                            <motion.div
                                                initial={{ opacity: 0, y: 15 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.6 }}
                                                className="bg-gray-50 p-5 rounded-xl hover:shadow-md transition-shadow border border-gray-100"
                                            >
                                                <div className="font-bold text-gray-800 text-2xl mb-1">
                                                    +40%
                                                </div>
                                                <div className="text-sm font-medium text-gray-600">
                                                    Trafic organique
                                                </div>
                                                <div className="mt-2 text-xs text-gray-500">
                                                    Augmentation du trafic via
                                                    les moteurs de recherche
                                                </div>
                                            </motion.div>
                                            <motion.div
                                                initial={{ opacity: 0, y: 15 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.7 }}
                                                className="bg-gray-50 p-5 rounded-xl hover:shadow-md transition-shadow border border-gray-100"
                                            >
                                                <div className="font-bold text-gray-800 text-2xl mb-1">
                                                    -65%
                                                </div>
                                                <div className="text-sm font-medium text-gray-600">
                                                    Temps de chargement
                                                </div>
                                                <div className="mt-2 text-xs text-gray-500">
                                                    Amélioration significative
                                                    des performances du site
                                                </div>
                                            </motion.div>
                                            <motion.div
                                                initial={{ opacity: 0, y: 15 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.8 }}
                                                className="bg-gray-50 p-5 rounded-xl hover:shadow-md transition-shadow border border-gray-100"
                                            >
                                                <div className="font-bold text-gray-800 text-2xl mb-1">
                                                    +25%
                                                </div>
                                                <div className="text-sm font-medium text-gray-600">
                                                    Taux de conversion
                                                </div>
                                                <div className="mt-2 text-xs text-gray-500">
                                                    Plus de visiteurs convertis
                                                    en clients ou leads
                                                </div>
                                            </motion.div>
                                        </>
                                    )}
                                </div>
                            </motion.div>

                            {/* Project details section */}
                            <motion.div
                                className="mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.9 }}
                            >
                                <h2 className="text-xl font-bold mb-4">
                                    Délais et livrables
                                </h2>
                                <div className="bg-gray-50 p-6 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 hover:bg-gray-50 shadow-sm">
                                                <svg
                                                    className="w-6 h-6 text-gray-500"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500">
                                                    Durée du projet
                                                </p>
                                                <p className="font-medium text-gray-800">
                                                    {project.duration ||
                                                        "6 semaines"}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 hover:bg-gray-50 shadow-sm">
                                                <svg
                                                    className="w-6 h-6 text-gray-500"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500">
                                                    Livrables
                                                </p>
                                                <p className="font-medium text-gray-800">
                                                    {project.deliverables ||
                                                        "Code source, documentation, formation"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        <motion.div
                            className="order-1 lg:order-2"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <div className="rounded-lg">
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-auto object-cover"
                                    loading="lazy"
                                />
                            </div>

                            {/* Additional project images */}
                            {project.additionalImages && (
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    {project.additionalImages.map(
                                        (img, index) => (
                                            <motion.div
                                                key={index}
                                                className="rounded-lg overflow-hidden"
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{
                                                    delay: 0.3 + index * 0.1,
                                                }}
                                            >
                                                <img
                                                    src={img}
                                                    alt={`${
                                                        project.title
                                                    } - Vue ${index + 1}`}
                                                    className="w-full h-auto object-cover"
                                                    loading="lazy"
                                                />
                                            </motion.div>
                                        )
                                    )}
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                </div>
            </main>
        </>
    );
};

export default ProjectDetail;
